// src/components/ProcessingResults/CropStage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect } from 'react-konva';
import { Spinner, Alert } from 'react-bootstrap';

const CropStage = ({
  jobData,
  originalImageRef,
  croppedImageRef,
  onStatusUpdate,
  onImageLoaded,
  isLoaded,
  skipProcessing
}) => {
  const [croppedImage, setCroppedImage] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [imageScale, setImageScale] = useState(1);
  const [plateBbox, setPlateBbox] = useState(null);
  const processedRef = useRef(''); // 'backend'/'frontend'


  useEffect(() => {
    if (croppedImageRef.current && !processedRef.current) {
        console.log("Crop from backend");
        processedRef.current = 'backend';
        const image = croppedImageRef.current;
        setCroppedImage(image);
        calculateStageSize(image);
        setPlateBbox(image, 0, 0, image.width, image.height);
        onImageLoaded();
        const isVertical = jobData.orientation === 1;
        onStatusUpdate(`Cropped image: W${image.width}xH${image.height} (${isVertical ? 'vertical' : 'horizontal'})`);
    } else if ((!processedRef.current || processedRef.current === 'frontend') && originalImageRef.current && jobData?.crop_bbox && jobData?.plate_bbox && !skipProcessing) {
      processedRef.current = 'frontend';
      createCroppedImage();
    }
  }, [originalImageRef.current, jobData, skipProcessing]);

  const createCroppedImage = () => {
    const originalImage = originalImageRef.current;
    const cropBbox = jobData.crop_bbox;
    const isVertical = jobData.orientation === 1; // 1 = vertical orientation

    if (!originalImage || !cropBbox) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    let rotatedImage;
    let imgWidth, imgHeight;

    if (isVertical) {
        // Step 1: Create rotated image (90° clockwise)
        const rotateCanvas = document.createElement('canvas');
        const rotateCtx = rotateCanvas.getContext('2d');

        // For 90° clockwise rotation: width becomes height, height becomes width
        rotateCanvas.width = originalImage.height;
        rotateCanvas.height = originalImage.width;

        rotateCtx.save();
        rotateCtx.translate(originalImage.height, 0);
        rotateCtx.rotate(Math.PI / 2);
        rotateCtx.drawImage(originalImage, 0, 0);
        rotateCtx.restore();

        // Create image from rotated canvas
        rotatedImage = rotateCanvas;
        imgWidth = rotateCanvas.width;
        imgHeight = rotateCanvas.height;
    } else {
        // No rotation needed
        rotatedImage = originalImage;
        imgWidth = originalImage.width;
        imgHeight = originalImage.height;
    }

    // Step 2: Crop the (possibly rotated) image using crop_bbox coordinates
    const cropX = cropBbox.x * imgWidth;
    const cropY = cropBbox.y * imgHeight;
    const cropWidth = cropBbox.width * imgWidth;
    const cropHeight = cropBbox.height * imgHeight;

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    ctx.drawImage(
        rotatedImage,
        cropX, cropY, cropWidth, cropHeight,  // source rectangle from rotated image
        0, 0, cropWidth, cropHeight           // destination rectangle
    );

    const img = new Image();
    img.onload = () => {
        setCroppedImage(img);
        croppedImageRef.current = img;

        // Calculate stage size
        const maxWidth = 800;
        const maxHeight = 600;
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

        setStageSize({
            width: img.width * scale,
            height: img.height * scale
        });

        // Calculate plate bbox relative to cropped image
        if (jobData.plate_bbox) {
            const plate = jobData.plate_bbox;

            // Plate coordinates in the rotated image space
            const plateX = plate.x * imgWidth;
            const plateY = plate.y * imgHeight;
            const plateWidth = plate.width * imgWidth;
            const plateHeight = plate.height * imgHeight;

            // Calculate plate position relative to crop
            const plateInCrop = {
                x: (plateX - cropX) / cropWidth,
                y: (plateY - cropY) / cropHeight,
                width: plateWidth / cropWidth,
                height: plateHeight / cropHeight
            };

            setPlateBbox({
                x: plateInCrop.x * img.width * scale,
                y: plateInCrop.y * img.height * scale,
                width: plateInCrop.width * img.width * scale,
                height: plateInCrop.height * img.height * scale
            });
        }

        onImageLoaded();
        onStatusUpdate(`Cropped image: W${img.width}xH${img.height} (${isVertical ? 'vertical' : 'horizontal'})`);
    };

    img.src = canvas.toDataURL();
  };

  const calculateStageSize = (img) => {
    const maxWidth = 800;
    const maxHeight = 600;

    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

    setStageSize({
      width: img.width * scale,
      height: img.height * scale
    });

    setImageScale(scale);
  };

  const calculatePlateBbox = (img, cropX, cropY, cropWidth, cropHeight) => {
    if (jobData.plate_bbox) {
      const maxWidth = 800;
      const maxHeight = 600;
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const plate = jobData.plate_bbox;
      // Plate coordinates in the rotated image space
      const plateX = plate.x * img.width;
      const plateY = plate.y * img.height;
      const plateWidth = plate.width * img.width;
      const plateHeight = plate.height * img.width;

      // Calculate plate position relative to crop
      const plateInCrop = {
          x: (plateX - cropX) / cropWidth,
          y: (plateY - cropY) / cropHeight,
          width: plateWidth / cropWidth,
          height: plateHeight / cropHeight
      };

      setPlateBbox({
          x: plateInCrop.x * img.width * scale,
          y: plateInCrop.y * img.height * scale,
          width: plateInCrop.width * img.width * scale,
          height: plateInCrop.height * img.height * scale
      });
    }
  };

  if (skipProcessing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center text-muted">
          <i className="bi bi-hourglass-split fs-1"></i>
          <div className="mt-2">Waiting for original image...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="crop-stage">
      <Stage width={stageSize.width} height={stageSize.height}>
        <Layer>
          {croppedImage && (
            <KonvaImage
              image={croppedImage}
              width={stageSize.width}
              height={stageSize.height}
            />
          )}

          {plateBbox && (
            <Rect
              x={plateBbox.x}
              y={plateBbox.y}
              width={plateBbox.width}
              height={plateBbox.height}
              stroke="red"
              strokeWidth={2}
              fill="transparent"
              onMouseEnter={(e) => {
                e.target.getStage().container().style.cursor = 'pointer';
                onStatusUpdate(`Plate detection boundary - Confidence: ${(jobData.plate_score).toFixed(2)*100}%`);
              }}
              onMouseLeave={(e) => {
                e.target.getStage().container().style.cursor = 'default';
                onStatusUpdate('');
              }}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default CropStage;